import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type {
  AccountSettings,
  ConfigurationSettings,
  SettingsSnapshot,
  SettingsTab,
  TeamMember,
  TeamRole,
  UsageRange,
} from '../../../../../data-sources/contract/settings';
import { Avatar } from '../../../atoms/Avatar/Avatar';
import { Badge, type BadgeType } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Checkbox } from '../../../atoms/Checkbox/Checkbox';
import { Icon } from '../../../atoms/Icon/Icon';
import { Slider } from '../../../atoms/Slider/Slider';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { Card } from '../../../molecules/Card/Card';
import { CardHeader } from '../../../molecules/CardHeader/CardHeader';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { InputField } from '../../../molecules/InputField/InputField';
import { KPI } from '../../../molecules/KPI/KPI';
import { Modal } from '../../../molecules/Modal/Modal';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { Select } from '../../../molecules/Select/Select';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Tabs } from '../../../molecules/Tabs/Tabs';
import { TextareaField } from '../../../molecules/TextareaField/TextareaField';
import styles from './SettingsWorkspace.module.css';

export const SETTINGS_TABS: ReadonlyArray<{ value: SettingsTab; label: string }> = [
  { value: 'account', label: 'Account' },
  { value: 'configurations', label: 'Configurations' },
  { value: 'sentiment-tuning', label: 'Sentiment tuning' },
  { value: 'team-permissions', label: 'Team permissions' },
  { value: 'usage', label: 'Usage' },
];

export function SettingsWorkspace({
  tab,
  tabs,
  error,
  children,
}: {
  tab: SettingsTab;
  /** The tabs to offer, in `SETTINGS_TABS` order. Omitted: every tab. */
  tabs?: readonly SettingsTab[];
  /** Why the last save failed, shown above the panel. */
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.settings}>
      <Tabs
        className={styles.tabs}
        label="Settings sections"
        value={tab}
        items={SETTINGS_TABS.filter((item) => !tabs || tabs.includes(item.value)).map((item) => ({
          ...item,
          href: `/settings/${item.value}`,
        }))}
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.panel}>{children}</div>
    </div>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
export function AccountSettingsForm({
  value,
  timezones,
  onChange,
  onChangePassword,
}: {
  value: AccountSettings;
  timezones: SettingsSnapshot['timezones'];
  onChange: (next: AccountSettings) => void;
  /**
   * Changes the password: resolves when it is changed, rejects with the reason
   * when it is not. Omitted: no `Change password` at all, rather than a form
   * whose Confirm changes nothing.
   */
  onChangePassword?: (oldPassword: string, newPassword: string) => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [changing, setChanging] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordPending, setPasswordPending] = useState(false);
  const [passwordError, setPasswordError] = useState<string>();
  const pickPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file?.type.startsWith('image/'))
      onChange({ ...value, photoUrl: URL.createObjectURL(file) });
  };
  const closePassword = () => {
    setChanging(false);
    setOldPassword('');
    setNewPassword('');
    setRevealed(false);
    setPasswordError(undefined);
  };
  const confirmPassword = async () => {
    if (!onChangePassword) return;
    setPasswordPending(true);
    setPasswordError(undefined);
    try {
      await onChangePassword(oldPassword, newPassword);
      closePassword();
    } catch (failure) {
      setPasswordError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setPasswordPending(false);
    }
  };
  const eye = (
    <Button
      hierarchy="ghostGrey"
      iconLeading={<Icon name="eyeOn" />}
      aria-label={revealed ? 'Hide passwords' : 'Show passwords'}
      aria-pressed={revealed}
      onClick={() => setRevealed((current) => !current)}
    />
  );
  return (
    <div className={styles.form}>
      <div className={styles.card}>
        <div className={styles.photoRow}>
          {value.photoUrl ? (
            <Avatar size="xl" src={value.photoUrl} alt="Your profile photo" />
          ) : (
            <Avatar size="xl" initials={initials(value.name)} />
          )}
          <Button hierarchy="ghostColor" onClick={() => fileRef.current?.click()}>
            Update profile photo
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={pickPhoto}
          />
        </div>
        <InputField
          className={styles.field}
          label="Account name"
          required
          value={value.name}
          onValueChange={(name) => onChange({ ...value, name })}
        />
        <InputField
          className={styles.field}
          label="Email address"
          required
          value={value.email}
          disabled
        />
        <Select
          className={styles.field}
          label="Timezone"
          required
          hint="Every date and time in the app is shown in this zone."
          value={value.timezone}
          onValueChange={(timezone) => onChange({ ...value, timezone })}
          options={timezones}
        />
        {changing ? (
          <>
            <InputField
              className={styles.field}
              label="Old password"
              required
              secure={!revealed}
              iconLeading={eye}
              value={oldPassword}
              onValueChange={setOldPassword}
            />
            <InputField
              className={styles.field}
              label="New password"
              required
              secure={!revealed}
              iconLeading={eye}
              value={newPassword}
              onValueChange={setNewPassword}
              errorMessage={passwordError}
            />
            <div className={styles.actions}>
              <Button hierarchy="tertiary" onClick={closePassword}>
                Cancel
              </Button>
              <Button
                hierarchy="secondary"
                disabled={passwordPending || !oldPassword || !newPassword}
                onClick={() => void confirmPassword()}
              >
                {passwordPending ? 'Changing...' : 'Confirm'}
              </Button>
            </div>
          </>
        ) : onChangePassword ? (
          <Button
            className={styles.hugStart}
            hierarchy="tertiary"
            onClick={() => setChanging(true)}
          >
            Change password
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ConfigurationSettingsForm({
  value,
  languageOptions,
  onChange,
}: {
  value: ConfigurationSettings;
  languageOptions: SettingsSnapshot['languageOptions'];
  onChange: (next: ConfigurationSettings) => void;
}) {
  return (
    <div className={styles.form}>
      <div className={styles.sections}>
        <section className={styles.card}>
          <CardHeader
            title="Monitoring"
            helpText="What the corpus is listened to for."
            helpSupportingText="A mention has to match one of these to enter any module."
          />
          <TextareaField
            className={styles.field}
            type="tags"
            label="Keywords and hashtags"
            required
            hint="You can add up to 10"
            placeholder="Add keywords and hashtags..."
            tags={value.keywords}
            onTagsChange={(keywords) => onChange({ ...value, keywords })}
          />
          <TextareaField
            className={styles.field}
            type="tags"
            label="Topics"
            required
            hint="You can add up to 10"
            placeholder="Add topics..."
            tags={value.topics}
            onTagsChange={(topics) => onChange({ ...value, topics })}
          />
          <Select
            className={styles.field}
            label="Language"
            required
            multiple
            options={languageOptions}
            values={value.languages}
            onValuesChange={(languages) => onChange({ ...value, languages })}
            placeholder="No languages selected"
          />
        </section>
        <section className={`${styles.card} ${styles.thresholdCard}`}>
          <CardHeader
            title="Sentiment threshold"
            helpText="Where a mention stops being neutral and becomes positive or negative."
            helpSupportingText="Narrowing the middle band makes the classifier commit more often."
          />
          <div className={styles.threshold}>
            <Slider
              className={styles.thresholdSlider}
              value={value.sentimentThreshold}
              onChange={(sentimentThreshold) => onChange({ ...value, sentimentThreshold })}
              labels={{ lower: 'Negative threshold', upper: 'Positive threshold' }}
            />
            <ChartLegend
              items={[
                { label: 'Positive', color: 'var(--progress-positive-surface)' },
                { label: 'Neutral', color: 'var(--progress-neutral-surface)' },
                { label: 'Negative', color: 'var(--progress-negative-surface)' },
              ]}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

const sentimentBadge: Record<'positive' | 'neutral' | 'negative', BadgeType> = {
  positive: 'positive',
  neutral: 'neutral',
  negative: 'negative',
};
const sentimentLabel = (value: string) => value[0]?.toUpperCase() + value.slice(1);
const SENTIMENT_ORDER = { negative: 0, neutral: 1, positive: 2 } as const;
export function SentimentTuningTable({ entries }: { entries: SettingsSnapshot['tuningLog'] }) {
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [sort, setSort] = useState<{
    key: 'previous' | 'current';
    direction: 'asc' | 'desc';
  } | null>(null);
  const rows = useMemo(() => {
    if (!sort) return entries;
    const next = [...entries].sort(
      (a, b) => SENTIMENT_ORDER[a[sort.key]] - SENTIMENT_ORDER[b[sort.key]],
    );
    return sort.direction === 'desc' ? next.reverse() : next;
  }, [entries, sort]);
  const toggleSort = (key: 'previous' | 'current') =>
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' },
    );
  const sortFor = (key: 'previous' | 'current'): TableHeaderSort =>
    sort?.key === key ? sort.direction : 'selector';
  const all = checked.size === entries.length && entries.length > 0;
  const columns: TableColumn[] = [
    {
      key: 'summary',
      header: 'Post summary',
      width: '58%',
      headerCheckbox: (
        <Checkbox
          checked={all}
          indeterminate={checked.size > 0 && !all}
          onChange={() => setChecked(all ? new Set() : new Set(entries.map((entry) => entry.id)))}
          aria-label="Select all corrections"
        />
      ),
    },
    {
      key: 'previous',
      header: 'Previous sentiment',
      width: '15%',
      sort: sortFor('previous'),
      onSort: () => toggleSort('previous'),
    },
    { key: 'arrow', width: '5%' },
    {
      key: 'current',
      header: 'Current sentiment',
      width: '15%',
      sort: sortFor('current'),
      onSort: () => toggleSort('current'),
    },
    { key: 'view', width: '7%' },
  ];
  const tableRows: TableRowData[] = rows.map((entry) => ({
    key: entry.id,
    selected: checked.has(entry.id),
    cells: {
      summary: {
        text: entry.summary,
        leadAction: (
          <Checkbox
            checked={checked.has(entry.id)}
            onChange={(on) =>
              setChecked((current) => {
                const next = new Set(current);
                if (on) next.add(entry.id);
                else next.delete(entry.id);
                return next;
              })
            }
            aria-label={`Select ${entry.summary}`}
          />
        ),
      },
      previous: {
        content: (
          <Badge type={sentimentBadge[entry.previous]}>{sentimentLabel(entry.previous)}</Badge>
        ),
      },
      arrow: { content: <Icon name="arrowRightLong" /> },
      current: {
        content: (
          <Badge type={sentimentBadge[entry.current]}>{sentimentLabel(entry.current)}</Badge>
        ),
      },
      view: {
        content: (
          <Button hierarchy="ghostColor" iconTrailing={<Icon name="arrowUpRight" />}>
            View
          </Button>
        ),
      },
    },
  }));
  return (
    <div className={styles.table}>
      <Table
        label="Sentiment tuning history"
        columns={columns}
        rows={tableRows}
        header={
          <TableHeader
            title="Sentiment tuning history"
            badge={`${entries.length} logs`}
            helpText="Every time somebody corrected the classifier."
            actions={
              <Button
                hierarchy="tertiary"
                iconLeading={<Icon name="revert" />}
                disabled={checked.size === 0}
                onClick={() => setChecked(new Set())}
              >
                Revert back to default
              </Button>
            }
          />
        }
      />
    </div>
  );
}

export type InviteValues = { email: string; role: TeamRole; modules: string[] };
export function InviteMemberModal({
  open,
  roles,
  modules: options,
  pending,
  error,
  onClose,
  onInvite,
}: {
  open: boolean;
  roles: SettingsSnapshot['roles'];
  modules: SettingsSnapshot['moduleOptions'];
  pending?: boolean;
  /** Why the last invite was refused, shown on the email field. */
  error?: string;
  onClose: () => void;
  onInvite: (values: InviteValues) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('admin');
  const [modules, setModules] = useState<string[]>([]);
  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('admin');
      setModules([]);
    }
  }, [open]);
  const needsModules = role !== 'super-admin';
  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<Icon name="invite" />}
      title="Invite a team member"
      description="They will get an email invitation. Until they accept it their status stays Pending."
      cancelLabel="Cancel"
      confirmLabel={pending ? 'Inviting...' : 'Invite'}
      confirmDisabled={pending || !email.trim() || (needsModules && modules.length === 0)}
      onConfirm={() => onInvite({ email, role, modules })}
    >
      <div className={styles.modalFields}>
        <InputField
          label="Email address"
          required
          inputMode="email"
          placeholder="name@company.com"
          value={email}
          onValueChange={setEmail}
          errorMessage={error}
        />
        <Select
          label="Role"
          required
          options={roles}
          value={role}
          onValueChange={(value) => setRole(value as TeamRole)}
          placement="auto"
        />
        {needsModules && (
          <Select
            label="Modules access"
            required
            multiple
            options={options}
            values={modules}
            onValuesChange={setModules}
            placeholder="Choose modules"
            placement="auto"
          />
        )}
      </div>
    </Modal>
  );
}

type TeamSortKey = 'name' | 'role' | 'status';
type TeamAction =
  { kind: 'invite' } | { kind: 'role' | 'revoke' | 'remove'; member: TeamMember } | null;
/**
 * A handler may return a promise: its dialog then stays open until it settles,
 * closing when it resolves and showing the rejection's message when it fails.
 */
type TeamHandlerResult = void | Promise<void>;
export function TeamPermissionsTable({
  snapshot,
  assignableRoles,
  onInvite,
  onUpdate,
  onRemove,
  onRevoke,
}: {
  snapshot: SettingsSnapshot;
  /** The roles the viewer may hand out, for the invite and permission dialogs. Omitted: all. */
  assignableRoles?: readonly TeamRole[];
  onInvite: (values: InviteValues) => TeamHandlerResult;
  onUpdate: (member: TeamMember, role: TeamRole, modules: string[]) => TeamHandlerResult;
  onRemove: (member: TeamMember) => TeamHandlerResult;
  onRevoke: (member: TeamMember) => TeamHandlerResult;
}) {
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<TeamAction>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const offeredRoles = assignableRoles
    ? snapshot.roles.filter((item) => assignableRoles.includes(item.value))
    : snapshot.roles;
  const closeDialog = () => {
    setDialog(null);
    setError(undefined);
  };
  const run = async (action: () => TeamHandlerResult) => {
    setPending(true);
    setError(undefined);
    try {
      await action();
      setDialog(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setPending(false);
    }
  };
  const dialogError = error && (
    <p className={styles.error} role="alert">
      {error}
    </p>
  );
  const [role, setRole] = useState<TeamRole>('admin');
  const [modules, setModules] = useState<string[]>([]);
  const visible = snapshot.teamMembers.filter((member) =>
    `${member.name} ${member.email} ${member.role}`
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  const openRole = (member: TeamMember) => {
    setRole(member.role);
    setModules(member.modules ?? []);
    setDialog({ kind: 'role', member });
  };
  const [sort, setSort] = useState<{ key: TeamSortKey; direction: 'asc' | 'desc' } | null>(null);
  const roleRank = new Map(snapshot.roles.map((item, index) => [item.value, index]));
  const compare: Record<TeamSortKey, (a: TeamMember, b: TeamMember) => number> = {
    name: (a, b) => a.name.localeCompare(b.name),
    role: (a, b) => (roleRank.get(a.role) ?? 0) - (roleRank.get(b.role) ?? 0),
    status: (a, b) => a.status.localeCompare(b.status),
  };
  const sorted = sort ? [...visible].sort(compare[sort.key]) : visible;
  if (sort?.direction === 'desc') sorted.reverse();
  const toggleSort = (key: TeamSortKey) =>
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  const sortable = (key: TeamSortKey) => ({
    sort: (sort?.key === key ? sort.direction : 'selector') as TableHeaderSort,
    onSort: () => toggleSort(key),
  });
  // Figma (2089:44455): Name, Email and Role share the width; Status and Actions hug.
  const columns: TableColumn[] = [
    { key: 'name', header: 'Name', width: '25.3%', ...sortable('name') },
    { key: 'email', header: 'Email', width: '25.3%' },
    { key: 'role', header: 'Role', width: '25.3%', ...sortable('role') },
    { key: 'status', header: 'Status', width: '7.9%', ...sortable('status') },
    { key: 'actions', header: 'Actions', width: '16.2%' },
  ];
  const labels = new Map(snapshot.roles.map((item) => [item.value, item.label]));
  const rows: TableRowData[] = sorted.map((member) => ({
    key: member.id,
    cells: {
      name: { text: member.self ? `${member.name} (You)` : member.name },
      email: { text: member.email },
      role: { text: labels.get(member.role) },
      status: {
        content: (
          <Badge type={member.status === 'active' ? 'positive' : 'neutral'}>
            {member.status === 'active' ? 'Active' : 'Pending'}
          </Badge>
        ),
      },
      actions: {
        content: (
          <span className={styles.rowActions}>
            {member.status === 'pending' ? (
              <Button hierarchy="ghostGrey" onClick={() => setDialog({ kind: 'revoke', member })}>
                Revoke invitation
              </Button>
            ) : (
              <Button
                hierarchy="ghostColor"
                disabled={member.self}
                onClick={() => openRole(member)}
              >
                Update permission
              </Button>
            )}
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="deleteBin" />}
              disabled={member.self}
              aria-label={`Remove ${member.name}`}
              onClick={() => setDialog({ kind: 'remove', member })}
            />
          </span>
        ),
      },
    },
  }));
  return (
    <div className={styles.table}>
      <Table
        label="Team members"
        columns={columns}
        rows={rows}
        emptyLabel="No team member matches that search."
        header={
          <TableHeader
            title="Team members"
            badge={`${snapshot.teamMembers.length} members`}
            helpText="Everyone who can open this workspace, and the role each one has."
            search={
              <Search
                className={styles.search}
                value={query}
                onValueChange={setQuery}
                placeholder="Search member..."
                aria-label="Search team members"
              />
            }
            actions={
              <Button
                hierarchy="secondary"
                iconLeading={<Icon name="invite" />}
                onClick={() => setDialog({ kind: 'invite' })}
              >
                Invite member
              </Button>
            }
          />
        }
      />
      <InviteMemberModal
        open={dialog?.kind === 'invite'}
        roles={offeredRoles}
        modules={snapshot.moduleOptions}
        pending={pending}
        error={error}
        onClose={closeDialog}
        onInvite={(values) => void run(() => onInvite(values))}
      />
      <Modal
        open={dialog?.kind === 'role'}
        onClose={closeDialog}
        icon={<Icon name="edit" />}
        title={
          dialog?.kind === 'role'
            ? `Update ${dialog.member.name}’s permission`
            : 'Update permission'
        }
        description="Their access changes as soon as you confirm."
        cancelLabel="Cancel"
        confirmLabel="Confirm"
        confirmDisabled={pending || (role !== 'super-admin' && modules.length === 0)}
        onConfirm={() => {
          if (dialog?.kind === 'role') {
            const { member } = dialog;
            void run(() => onUpdate(member, role, modules));
          }
        }}
      >
        <div className={styles.modalFields}>
          <Select
            label="Role"
            required
            options={offeredRoles}
            value={role}
            onValueChange={(value) => setRole(value as TeamRole)}
          />
          {role !== 'super-admin' && (
            <Select
              label="Modules access"
              required
              multiple
              options={snapshot.moduleOptions}
              values={modules}
              onValuesChange={setModules}
            />
          )}
          {dialogError}
        </div>
      </Modal>
      <Modal
        open={dialog?.kind === 'revoke'}
        onClose={closeDialog}
        icon={<Icon name="revert" />}
        title={
          dialog?.kind === 'revoke'
            ? `Revoke ${dialog.member.name}’s invitation?`
            : 'Revoke invitation?'
        }
        description="Their invitation link stops working straight away."
        destructive
        cancelLabel="Cancel"
        confirmLabel="Revoke invitation"
        confirmDisabled={pending}
        onConfirm={() => {
          if (dialog?.kind === 'revoke') {
            const { member } = dialog;
            void run(() => onRevoke(member));
          }
        }}
      >
        {dialogError}
      </Modal>
      <Modal
        open={dialog?.kind === 'remove'}
        onClose={closeDialog}
        icon={<Icon name="deleteBin" />}
        title={
          dialog?.kind === 'remove'
            ? `Remove ${dialog.member.name} from workspace?`
            : 'Remove from workspace?'
        }
        description="They lose access immediately. This action cannot be undone."
        destructive
        cancelLabel="Cancel"
        confirmLabel="Remove"
        confirmDisabled={pending}
        onConfirm={() => {
          if (dialog?.kind === 'remove') {
            const { member } = dialog;
            void run(() => onRemove(member));
          }
        }}
      >
        {dialogError}
      </Modal>
    </div>
  );
}

export function UsageSettings({
  snapshot,
  creditsLeft,
}: {
  snapshot: SettingsSnapshot;
  creditsLeft: number;
}) {
  const [range, setRange] = useState<UsageRange>('1d');
  const names = new Map(snapshot.teamMembers.map((member) => [member.id, member.name]));
  const series = snapshot.usage[range];
  const used = series.reduce((total, point) => total + point.credits, 0);
  const rows: TableRowData[] = snapshot.usageLog.map((entry) => ({
    key: entry.id,
    cells: {
      date: {
        text: new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(entry.at)),
      },
      query: { text: entry.query },
      credits: { text: String(entry.credits) },
      user: { content: <Badge type="gray">{names.get(entry.memberId) ?? entry.memberId}</Badge> },
    },
  }));
  const columns: TableColumn[] = [
    { key: 'date', header: 'Date', width: '25%' },
    { key: 'query', header: 'Query' },
    { key: 'credits', header: 'Credit used', width: '18%' },
    { key: 'user', header: 'User', width: '18%' },
  ];
  return (
    <div className={`${styles.form} ${styles.stack}`}>
      <Table
        label="Log"
        columns={columns}
        rows={rows}
        header={
          <TableHeader
            title="Log"
            badge={`${rows.length} logs`}
            badgeType="info"
            helpText="DeepDive AI chats and the credits each one spent."
          />
        }
        pagination={<Pagination page={1} pageCount={1} pages={[1]} label="Log pages" />}
      />
      <Card
        title="AI credit usage tracker"
        helpText="Credits spent on DeepDive AI answers, one per answer."
        tabs={{
          items: [
            { value: '1d', label: '1d' },
            { value: '7d', label: '7d' },
            { value: '30d', label: '30d' },
          ],
          value: range,
          onChange: (value) => setRange(value as UsageRange),
          label: 'Usage period',
        }}
        kpi={
          <KPI
            metrics={[
              { label: 'Credits used', value: used.toLocaleString() },
              { label: 'Credits left', value: creditsLeft.toLocaleString() },
            ]}
          />
        }
        chart={
          <BarChart
            data={series}
            xKey="label"
            series={[{ key: 'credits', label: 'Credits used' }]}
            legend="none"
            xTicksTarget={range === '1d' ? 8 : range === '7d' ? 7 : 6}
            label="Credits used"
          />
        }
      />
    </div>
  );
}
